import { useState } from "react";
import { mockAgents, mockSessions, USE_MOCK_DATA } from "./mockData";
import { useWorkflowAgents, useWorkflowSessions, useSendCommand } from "./useWorkflowQueries";
import { AgentConfigTabContent, type ConfigTab } from "./WorkflowAgentConfigTabs";
import styles from "./workflow.module.scss";

interface Props {
  agentId: string;
  onBack: () => void;
  lastRefreshed: Date;
}

const CONFIG_TABS: { key: ConfigTab; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "config", label: "Configuration" },
  { key: "sessions", label: "Sessions" },
  { key: "logs", label: "Logs" },
];

const WorkflowAgentConfig = ({ agentId, onBack, lastRefreshed: _lastRefreshed }: Props) => {
  const [activeTab, setActiveTab] = useState<ConfigTab>("overview");

  // Data sources
  const { data: apiAgents } = useWorkflowAgents();
  const agents = USE_MOCK_DATA ? mockAgents : (apiAgents ?? []);
  const agent = agents.find(a => a.id === agentId);

  const { data: apiSessionsData } = useWorkflowSessions();
  const sessions = USE_MOCK_DATA ? mockSessions : (apiSessionsData?.sessions ?? []);
  const agentSessions = sessions.filter(s => s.current_agent_id === agentId);

  const restartMutation = useSendCommand();

  if (!agent) {
    return <div>Agent not found</div>;
  }

  return (
    <div className={styles.configPage}>
      <button type="button" className={styles.backBtn} onClick={onBack}>
        &larr; Back to Dashboard
      </button>

      <div className={styles.agentConfigHeader}>
        <h2 className={styles.agentConfigTitle}>{agent.name}</h2>
        <span className={`${styles.statusBadge} ${styles[agent.enabled ? "online" : "offline"]}`}>
          {agent.enabled ? "enabled" : "disabled"}
        </span>
        <button
          type="button"
          className={styles.restartBtn}
          onClick={() => restartMutation.mutate({ command: "restart" })}
          disabled={!agent.enabled || restartMutation.isPending}
        >
          {restartMutation.isPending ? "Restarting..." : "Restart"}
        </button>
      </div>

      <div className={styles.configTabNav}>
        {CONFIG_TABS.map(tab => (
          <button
            key={tab.key}
            type="button"
            className={`${styles.configTab} ${activeTab === tab.key ? styles.active : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className={styles.configTabContent}>
        <AgentConfigTabContent
          activeTab={activeTab}
          agent={agent}
          agentSessions={agentSessions}
        />
      </div>
    </div>
  );
};

export default WorkflowAgentConfig;
